import { type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

type ColorVariant = 'blue' | 'green' | 'yellow' | 'purple' | 'orange' | 'red' | 'muted';

const colorMap: Record<ColorVariant, string> = {
  blue: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  green: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  yellow: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
  purple: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  orange: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
  red: 'bg-red-500/10 text-red-600 dark:text-red-400',
  muted: 'bg-muted text-muted-foreground',
};

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  value: string | number;
  color?: ColorVariant;
  trend?: { value: number; label?: string };
  tint?: string;
  className?: string;
  animationDelay?: string;
}

export function StatCard({ icon: Icon, label, value, color, trend, tint, className, animationDelay }: StatCardProps) {
  // Color variant: old system style (icon left, value+label right)
  if (color) {
    return (
      <div
        className={cn(
          'flex items-center gap-3 p-4 rounded-xl',
          'bg-background/50 backdrop-blur-sm border border-border',
          'animate-fade-in-up opacity-0',
          'hover:bg-background/70 transition-all duration-300',
          className,
        )}
        style={{ animationDelay, animationFillMode: 'forwards' }}
      >
        <div className={cn('p-2 rounded-lg', colorMap[color])}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </div>
    );
  }

  // Default: dashboard-style (label top, value large, icon top-right)
  return (
    <div
      className={cn('rounded-xl border bg-card p-4 shadow-xs', className)}
      style={tint ? { backgroundColor: tint } : undefined}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-muted-foreground">{label}</p>
          <p className="mt-1 text-3xl font-bold tracking-tight">{value}</p>
          {trend && (
            <p className={cn('mt-1 text-xs font-medium', trend.value >= 0 ? 'text-emerald-600' : 'text-red-600')}>
              {trend.value >= 0 ? '+' : ''}{trend.value}%{trend.label ? ` ${trend.label}` : ''}
            </p>
          )}
        </div>
        <div className="rounded-lg bg-muted p-2">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
      </div>
    </div>
  );
}
